import React from "react";
import {Chip, Tooltip} from "@mui/material";
import Loader from "../../../../common/components/shared/loader/loader";
import Table, {TableActionFunction} from "../../../../common/components/shared/table/table";
import {Api} from "../../../../helpers/api";
import i18n from "../../../../i18n/i18n";
import {DateUtils} from "../../../../helpers/dateUtils";
import {HttpError} from "../../../../common/components/shared/httpError/httpError";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../../common/models/httpError";
import {NavLink, withRouter} from "react-router-dom";
import {routingAddresses} from "../../../../helpers/routing";
import "../../transportation.scss";
import {TicketModel} from "../../../models/ticket";
import Button from "../../../../common/components/shared/button/button";
import ReloadButton from "../../../../common/components/shared/reloadButton/reloadButton";
import {storesContext} from "../../../../common/stores/stores";
import {IPaginationStore} from "../../../../common/stores/paginationStore";
import {TicketService} from "../../../services/ticketService";
import TransportUtils from "../../../../helpers/transportUtils";
import {IUserStore} from "../../../../administration/stores/userStore";
import ScanBarcodePopup from "../../scanBarcodePopup/scanBarcodePopup";
import {ReactComponent as ScanBarcodeIcon} from "../../../../statics/icons/icon_scanBarcode.svg";
import {ValidatedValue} from "../../../../helpers/validatedValue";
import {OrderStatusEnum} from "../../../models/order";
import {RelationType} from "../../../models/relation";
import TransportationFilters from "../../transportationFilters/transportationFilters";
import WarningFiltersPopup from "../../../../common/components/shared/warningFiltersPopup/warningFiltersPopup";
import Validator from "../../../../helpers/validator";
import {SortDirection} from "../../../../common/models/table";
import Notification from "../../../../common/components/shared/notification/notification";
import {errorMessages} from "../../../../helpers/errorMessage";
import SearchButton from "../../../../common/components/shared/searchButton/searchButton";

interface TicketListState {
    ticketData: TicketModel[] | undefined | null;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    currentPage: number;
    isTableLoader: boolean;
    displayPopup: "scanBarcode" | "filterWarning" | null;
    selectedDateOfRegistrationFrom: ValidatedValue<Date | null>;
    selectedDateOfRegistrationTo: ValidatedValue<Date | null>;
    selectedArrivalDepartureTime: ValidatedValue<Date | null>;
    selectedFerry: ValidatedValue<string>;
    selectedOrderStatus: ValidatedValue<OrderStatusEnum | "">;
    selectedRelationType: ValidatedValue<RelationType | "">;
    orderBy: string | undefined;
    sortDirection: SortDirection | undefined;
    scanBarcodeResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    isPopupActionWaiting: boolean;
    barcodeNo: ValidatedValue<string>;
    isScanBarcodeSuccess: boolean;
    isLoading: boolean;
    searchInput: ValidatedValue<string>;
    isSearchLoader: boolean;
}

class TicketList extends React.Component<any, TicketListState> {
    static contextType = storesContext;

    private sortableElements = ["shipownerName", "ferry", "arrivalDepartureTime", "createdAt", "regNo"];

    constructor(props: any) {
        super(props);

        const currentDataWithTime = new Date();
        currentDataWithTime.setMonth(currentDataWithTime.getMonth() - 1);
        currentDataWithTime.setSeconds(0);
        currentDataWithTime.setMilliseconds(0);

        const notEmptyValidator = {
            name: "valueIsNotEmpty",
            fn: Validator.isNotEmpty,
            errorMessage: errorMessages.required
        };

        this.state = {
            ticketData: undefined,
            response: undefined,
            currentPage: 0,
            isTableLoader: false,
            displayPopup: null,
            selectedDateOfRegistrationFrom: new ValidatedValue(currentDataWithTime),
            selectedDateOfRegistrationTo: new ValidatedValue(null),
            selectedArrivalDepartureTime: new ValidatedValue(null),
            selectedFerry: new ValidatedValue(""),
            selectedOrderStatus: new ValidatedValue(""),
            selectedRelationType: new ValidatedValue(""),
            orderBy: undefined,
            sortDirection: undefined,
            scanBarcodeResponse: undefined,
            isPopupActionWaiting: false,
            barcodeNo: new ValidatedValue("", [notEmptyValidator]),
            isScanBarcodeSuccess: false,
            isLoading: false,
            searchInput: new ValidatedValue(""),
            isSearchLoader: false
        };
    }

    private get ticketData() {
        return this.state.ticketData;
    }

    public async componentDidMount() {
        await this.getAllTickets(false);
    }

    public render(): JSX.Element {
        const properties = ["status", "shipownerName", "ferry", "arrivalDepartureTime",
            "createdAt", "relationType", "regNo", "gateNo", "cargoType", "info"];
        const headers = properties.map(key => i18n.t("transportation.ticket.headers." + key));
        const actions = this.renderActions;

        return (
            <div className="transportation-ticket">
                {this.renderWrapperContent(headers, properties, "transportation-ticket", actions, this.renderCell.bind(this))}
            </div>
        );
    }

    private renderWrapperContent(headers: string[], properties: string[], testId: string, actions?: TableActionFunction, renderCell?: (row: any, property: string) => JSX.Element) {
        if (!Api.isSuccess(this.state.response)) {
            return (
                <HttpError
                    response={this.state.response}
                    cause={i18n.t("transportation.ticket.retrievalErrorSuffix")}
                />
            );
        } else {
            if (this.ticketData === undefined || this.state.isLoading) {
                return <Loader/>;
            } else {
                return (
                    <>
                        {this.renderHeader()}
                        <div className="transportation-ticket-content">
                            {this.ticketData && this.ticketData!.length && this.ticketData!.length > 0 ? (
                                <Table
                                    headers={headers}
                                    rows={this.state.ticketData!}
                                    properties={properties}
                                    testId={`${testId}-table`}
                                    actions={actions ? actions.bind(this) : actions}
                                    renderCell={renderCell}
                                    pageable={true}
                                    currentPage={this.state.currentPage}
                                    onPageChange={this.getAllTickets.bind(this, false)}
                                    isOldPagination={false}
                                    isTableLoader={this.state.isTableLoader}
                                    sortableElements={this.sortableElements}
                                    sortingAction={this.sortingAction.bind(this)}
                                    orderBy={this.state.orderBy}
                                    sortDirection={this.state.sortDirection}
                                />
                            ) : (
                                <Table
                                    currentPage={0}
                                    headers={headers}
                                    rows={[]}
                                    properties={properties}
                                    testId={`${testId}-table`}
                                    numberOfElements={0}
                                />
                            )}
                        </div>
                        {this.renderScanBarcodePopup()}
                        {this.renderWarningPopup()}
                        {this.state.isScanBarcodeSuccess && (
                            <Notification
                                message={
                                    <>{i18n.t("transportation.ticket.scanBarcodePopup.successMessage")}</>
                                }
                                type="success"
                                onClose={() => {
                                    this.setState({scanBarcodeResponse: undefined, isScanBarcodeSuccess: false});
                                }}
                            />
                        )}
                    </>
                );
            }
        }
    }

    private renderHeader() {
        const userStore = this.context.userStore as IUserStore;

        return (
            <div className="header">
                <div className="filters-content">
                    <TransportationFilters
                        element="ticket"
                        selectedDateOfRegistrationFrom={this.state.selectedDateOfRegistrationFrom}
                        selectedDateOfRegistrationTo={this.state.selectedDateOfRegistrationTo}
                        selectedArrivalDepartureTime={this.state.selectedArrivalDepartureTime}
                        selectedFerry={this.state.selectedFerry}
                        selectedOrderStatus={this.state.selectedOrderStatus}
                        selectedRelationType={this.state.selectedRelationType}
                        onDateTimePickerChange={(newDate: Date | null, isDateFrom: boolean, isDateTo: boolean) => {
                            this.dateTimePickerOnChange(newDate, isDateFrom, isDateTo);
                        }}
                        setIsLoading={(value: boolean) => {
                            this.setState({isLoading: value});
                        }}
                        searchInput={this.state.searchInput}
                    />
                </div>
                <div className="actions">
                    <SearchButton
                        onClick={async () => {
                            if (this.state.selectedDateOfRegistrationFrom.value || this.state.selectedDateOfRegistrationTo.value) {
                                await this.whetherToRunFiltering();
                            }
                        }}
                        isWaiting={this.state.isSearchLoader}
                    />
                    {/* TODO: Uncomment when adding a ticket is required */}
                    {/* {userStore.canModifyTicket && (
                        <NavLink to={routingAddresses.ticket.add} data-testid="add-ticket">
                            <Button type="primary" text={i18n.t("transportation.ticket.addTicket.button")} isWaiting={ticketStore.isLoading.value}/>
                        </NavLink>
                    )} */}
                    {userStore.canScanTicketBarcode && (
                        <Button
                            type="primary"
                            onClick={() => {
                                this.setState({displayPopup: "scanBarcode"});
                            }}
                            text={i18n.t("transportation.ticket.actions.scanTicket")}
                            endIcon={<ScanBarcodeIcon/>}
                            testId="scan-ticket"
                        />
                    )}
                    <ReloadButton
                        reloadFunc={this.reload.bind(this)}
                        testId="ticket-list-reload-btn"
                    />
                </div>
            </div>
        );
    }

    private renderCell(row: any, property: string): JSX.Element {
        switch (property) {
            case "status": {
                const statusName = i18n.t(`orderStatus.${row[property]}`);
                return (
                    <Tooltip title={ statusName || ""} arrow>
                        <Chip
                            className={`${row[property]}`.toLocaleLowerCase()}
                            label={statusName}
                        />
                    </Tooltip>
                );
            }
            case "relationType":
                return <>{i18n.t(`relationType.${row[property]}`)}</>;
            case "arrivalDepartureTime":
            case "createdAt":
                return <>{DateUtils.getFormattedDateWithTime(row[property])}</>;
            case "info":
                return <>{TransportUtils.renderInfoIcons(row)}</>;
            case "ferry":
                return <>{row[property].name}</>;
            case "cargoType":
                return <>{i18n.t(`cargoType.${row[property]}`)}</>;
            default:
                return row[property];
        }
    }

    private renderActions(element: TicketModel) {
        // @ts-ignore
        const elementId = element.id;
        const userStore = this.context.userStore as IUserStore;

        return (
            <>
                {elementId && (
                    <>
                        <NavLink
                            to={`${routingAddresses.ticket.details}/${elementId}`} className="action-button"
                            data-testid={`preview-${elementId}`}
                        >
                            {i18n.t("preview")}
                        </NavLink>
                        {userStore.canModifyTicket && (
                            <NavLink
                                className="action-button"
                                to={`${routingAddresses.ticket.modify}/${elementId}`}
                                data-testid={`modify-ticket-${elementId}`}
                            >
                                {i18n.t("transportation.ticket.modifyTicket.button")}
                            </NavLink>
                        )}
                    </>
                )}
            </>
        );
    }

    private renderScanBarcodePopup() {
        return (
            <ScanBarcodePopup
                barcodeNo={this.state.barcodeNo}
                displayPopup={this.state.displayPopup === "scanBarcode"}
                closePopup={this.closePopup.bind(this)}
                confirm={this.confirmScanBarcode.bind(this)}
                response={this.state.scanBarcodeResponse}
                isPopupActionWaiting={this.state.isPopupActionWaiting}
            />
        );
    }

    protected renderWarningPopup() {
        return (
            <WarningFiltersPopup
                displayPopup={this.state.displayPopup === "filterWarning"}
                actionPopup={async () => {
                    await this.getAllTickets(true);
                    this.setState({displayPopup: null});
                }}
                closePopup={this.closePopup.bind(this)}
            />
        );
    }

    private dateTimePickerOnChange(newDate: Date | null, isDateFrom: boolean, isDateTo: boolean) {
        if (isDateFrom) {
            this.state.selectedDateOfRegistrationFrom.setValue(newDate);
        } else if(isDateTo) {
            this.state.selectedDateOfRegistrationTo.setValue(newDate);
        } else {
            this.state.selectedArrivalDepartureTime.setValue(newDate);
        }
        Validator.addValidatorsToDateTimePickerForOrdersAndTickets(this.state.selectedDateOfRegistrationFrom, this.state.selectedDateOfRegistrationTo, this.state.selectedArrivalDepartureTime);
        if (!this.state.selectedDateOfRegistrationFrom.value && !this.state.selectedDateOfRegistrationTo.value && !this.state.selectedArrivalDepartureTime) {
            this.setState({displayPopup: "filterWarning"});
        }
    }

    private async whetherToRunFiltering() {
        this.setState({isSearchLoader: true});
        if (TransportUtils.dateToIsGreaterThanTheMonthBefore(this.state.selectedDateOfRegistrationFrom, this.state.selectedDateOfRegistrationTo)) {
            this.setState({displayPopup: "filterWarning"});
        } else {
            await this.getAllTickets(true);
        }
        this.setState({isSearchLoader: false});
    }

    public get isFilterValid() {
        this.state.selectedDateOfRegistrationFrom.validate();
        this.state.selectedDateOfRegistrationTo.validate();
        this.state.selectedArrivalDepartureTime.validate();
        this.state.selectedFerry.validate();
        this.state.selectedOrderStatus.validate();
        this.state.selectedRelationType.validate();

        return this.state.selectedDateOfRegistrationFrom.isValid && this.state.selectedDateOfRegistrationTo.isValid &&
            this.state.selectedArrivalDepartureTime.isValid && this.state.selectedFerry.isValid && this.state.selectedOrderStatus && this.state.selectedRelationType.isValid;
    }

    private async getAllTickets(isFilter: boolean) {
        const paginationStore = this.context.paginationStore as IPaginationStore;
        const selectedDateOfRegistrationFrom = this.state.selectedDateOfRegistrationFrom.value;
        const selectedDateOfRegistrationTo = this.state.selectedDateOfRegistrationTo.value;
        const selectedArrivalDepartureTime = this.state.selectedArrivalDepartureTime.value;
        const dateToIsGreaterThanTheMonthBefore = TransportUtils.dateToIsGreaterThanTheMonthBefore(
            this.state.selectedDateOfRegistrationFrom, this.state.selectedDateOfRegistrationTo);

        this.setState({response: undefined, ticketData: undefined, isTableLoader: true});

        if (!this.isFilterValid) {
            return;
        }

        if (!selectedDateOfRegistrationFrom && !selectedDateOfRegistrationTo && !this.state.displayPopup) {
            this.setState({displayPopup: "filterWarning", ticketData: []});
        } else if (dateToIsGreaterThanTheMonthBefore && !this.state.displayPopup) {
            this.setState({displayPopup: "filterWarning", ticketData: []});
        } else {
            const filters = {
                dateOfRegistrationFrom: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedDateOfRegistrationFrom.value),
                dateOfRegistrationTo: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedDateOfRegistrationTo.value),
                arrivalDepartureTime: DateUtils.getFormattedDateWithTimeInUTC(selectedArrivalDepartureTime),
                ferryEntityId: this.state.selectedFerry.value,
                relationType: this.state.selectedRelationType.value,
                status: this.state.selectedOrderStatus.value
            };

            const response = await TicketService.getAllTickets(
                filters,
                {
                    offset: isFilter ? 0 : paginationStore.offset,
                    size: paginationStore.size
                },
                {
                    orderBy: this.state.orderBy,
                    sortDirection: this.state.sortDirection
                },
                this.state.searchInput.value.trim()
            );

            this.setState({
                ticketData: response?.data?.data || null,
                response,
                currentPage: response?.data?.currentPage,
                isTableLoader: false
            });

            paginationStore.setNumberOfElements(response?.data?.numberOfElements);
            paginationStore.setNumberOfPages(response?.data?.numberOfPages);
        }
    }

    private async reload() {
        this.setState({ticketData: undefined});
        await this.getAllTickets(false);
    }

    private closePopup() {
        this.state.barcodeNo.setValue("");
        this.state.barcodeNo.clearValidation();
        this.setState({displayPopup: null, scanBarcodeResponse: undefined});
    }

    private async sortingAction(orderBy: string, sortDirection: SortDirection) {
        if (orderBy === "ferry") {
            orderBy = "ferry.name";
        }

        await this.setState({orderBy, sortDirection});
        await this.getAllTickets(false);
    }

    private get barcodeScanIsValid() {
        this.state.barcodeNo.validate();

        return this.state.barcodeNo.isValid;
    }

    private async confirmScanBarcode() {
        this.setState({
            scanBarcodeResponse: undefined,
            isPopupActionWaiting: true
        });

        if (this.barcodeScanIsValid) {
            const response = await TicketService.scanBarcode(this.state.barcodeNo.value.trim());

            this.setState({scanBarcodeResponse: response});

            if (Api.isSuccess(response)) {
                this.setState({isScanBarcodeSuccess: true});
                this.reload();

                this.closePopup();
            }
        }

        this.setState({isPopupActionWaiting: false});
    }
}

export default withRouter(TicketList);
